/**
 * Browser fingerprinting module.
 * Generates a stable hash from canvas, WebGL, audio, fonts, and screen properties.
 * Used to identify unique visitors without cookies.
 */

/**
 * Generate a SHA-256 fingerprint from browser properties.
 * Returns a hex string like "a1b2c3d4...".
 */
export async function generateFingerprint(): Promise<string> {
	const components: string[] = [];

	// Canvas fingerprint
	try {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		if (ctx) {
			canvas.width = 200;
			canvas.height = 50;
			ctx.textBaseline = "top";
			ctx.font = "14px 'Arial'";
			ctx.fillStyle = "#f60";
			ctx.fillRect(125, 1, 62, 20);
			ctx.fillStyle = "#069";
			ctx.fillText("Yayy!fingerprint", 2, 15);
			ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
			ctx.fillText("Yayy!fingerprint", 4, 17);
			components.push(`canvas:${canvas.toDataURL()}`);
		}
	} catch {
		components.push("canvas:unsupported");
	}

	// WebGL fingerprint
	try {
		const gl = document.createElement("canvas").getContext("webgl");
		if (gl) {
			const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
			if (debugInfo) {
				const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
				const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
				components.push(`webgl:${vendor}|${renderer}`);
			} else {
				components.push("webgl:no-debug-info");
			}
		} else {
			components.push("webgl:unsupported");
		}
	} catch {
		components.push("webgl:error");
	}

	// Audio context fingerprint (needs user gesture in Chrome ≥66)
	try {
		const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
		if (AudioContext) {
			const audioCtx = new AudioContext();
			if (audioCtx.state === "suspended") {
				await audioCtx.resume();
			}
			const oscillator = audioCtx.createOscillator();
			const analyser = audioCtx.createAnalyser();
			const gain = audioCtx.createGain();

			gain.gain.value = 0; // mute
			oscillator.connect(analyser);
			analyser.connect(gain);
			gain.connect(audioCtx.destination);

			oscillator.start(0);

			// Let the audio pipeline produce stable data before sampling.
			await new Promise<void>((resolve) => {
				setTimeout(() => {
					const audioData = new Float32Array(analyser.frequencyBinCount);
					analyser.getFloatFrequencyData(audioData);
					components.push(`audio:${audioData.slice(0, 10).join(",")}`);
					oscillator.stop();
					audioCtx.close().then(() => resolve()).catch(() => resolve());
				}, 100);
			});
		} else {
			components.push("audio:unsupported");
		}
	} catch {
		components.push("audio:error");
	}

	// Screen properties
	components.push(
		`screen:${screen.width}x${screen.height}x${screen.colorDepth}`,
	);

	// Timezone
	components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

	// Language
	components.push(`lang:${navigator.language}`);

	// Platform
	components.push(`platform:${navigator.platform}`);

	// Hardware concurrency
	components.push(`cores:${navigator.hardwareConcurrency || 0}`);

	// Device memory (Chrome only)
	const nav = navigator as any;
	if (nav.deviceMemory !== undefined) {
		components.push(`memory:${nav.deviceMemory}`);
	}

	// Touch support
	components.push(`touch:${navigator.maxTouchPoints || 0}`);

	// Do Not Track
	components.push(`dnt:${navigator.doNotTrack}`);

	// Combine and hash
	const raw = components.join("|||");
	return sha256(raw);
}

/**
 * SHA-256 hash using the Web Crypto API.
 */
async function sha256(message: string): Promise<string> {
	const msgBuffer = new TextEncoder().encode(message);
	const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
